import { Flex, FlexProps, Text } from '@chakra-ui/react'
import type { ConditionalValue, SystemStyleObject } from '@chakra-ui/react'
import { ArrowUpDown, Pause, Phone, PhoneOff, Play, X } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TrackedParkingLot } from '~/atoms/WebphoneState'
import {
  MangoButton,
  MangoButtonProps,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { Tooltip } from '~/components/ui/tooltip'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useCallPopEnabled } from '~/hooks/useCallPopEnabled'
import { useMainWindow } from '~/hooks/useElectronWindow'
import { useSharedVariable } from '~/hooks/useSharedVariable'
import { IncomingCallButtonVariants } from '~/pages/WebPhone/componentStyles/IncomingCallButton.style'
import { ParkCallButton } from '~/pages/WebPhone/IncomingCallButton'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'
import { Size } from '~/types/ThemeTypes'
import { noop } from '~/utils/functions'

import {
  CallQueueItem,
  isPreConnectedStatus,
  PhoneCall,
  PhoneCallStatus,
  PopupQueueItemTypes,
} from '@mangovoice/types'

type CallAnswerType =
  'answer' | 'answerWithHold' | 'answerWithEnd' | 'answerWithPark'

const customMangoButtonProps = {
  size: Size.md,
  leftIconProps: {
    boxSize: 4,
  },
  rightIconProps: {
    boxSize: 4,
  },
  padding: '0.5rem',
  width: 'auto',
  flexGrow: 1,
  minWidth: '40px',
} as Partial<MangoButtonProps>

const {
  leftIconProps: _,
  rightIconProps: __,
  ...buttonPropsWithoutIcons
} = customMangoButtonProps

export const MangoButtonPopup = (props: {
  onClick: () => void
  title: string
  /** Tooltip text and accessible name; defaults to `title`. */
  ariaLabel?: string
  leftIcon?: SVGComponent | null
  leftIconProps?: SystemStyleObject
  rightIcon?: SVGComponent
  rightIconProps?: SystemStyleObject
  variant?: MangoButtonVariant
  size?: ConditionalValue<Size.md | Size.sm>
  disabled?: boolean
  mt?: number
  customMangoButtonProps?: Partial<MangoButtonProps>
}) => {
  const {
    title,
    ariaLabel,
    onClick,
    variant,
    leftIcon,
    leftIconProps = {},
    rightIcon,
    rightIconProps = {},
    size,
    disabled,
    mt,
    // Aliased: shares a name with the module-level default constant below.
    // size and mt are applied after this spread and can't be overridden
    // through it — use the size/mt props instead.
    customMangoButtonProps: customMangoButtonPropsOverride = {},
  } = props

  const label = ariaLabel ?? title

  return (
    <Tooltip content={label}>
      <MangoButton
        aria-label={label}
        leftIcon={leftIcon === null ? undefined : (leftIcon ?? Phone)}
        leftIconProps={{
          ...customMangoButtonProps.leftIconProps,
          ...leftIconProps,
        }}
        rightIcon={rightIcon}
        rightIconProps={{
          ...customMangoButtonProps.rightIconProps,
          ...rightIconProps,
        }}
        variant={variant}
        disabled={disabled}
        onClick={onClick}
        {...buttonPropsWithoutIcons}
        {...customMangoButtonPropsOverride}
        size={size ?? (Size.sm as any)}
        mt={mt}
      />
    </Tooltip>
  )
}

export const CallButtons = (props: { callItem: CallQueueItem } & FlexProps) => {
  const { callItem, ...rest } = props
  const { t } = useTranslation()
  const effectiveCallPopEnabled = useCallPopEnabled()
  const {
    answerPhoneCall,
    rejectPhoneCall,
    answerCallWithEnd,
    answerCallWithHold,
    answerCallWithParkCurrent,
    holdCall,
    resumeCall,
    endCall,
    switchCall,
    dismissCall,
  } = useMainWindow()
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const [parkingLots] = useSharedVariable<TrackedParkingLot[]>('ParkingLots')
  const [sharedCallList] = useSharedVariable<PhoneCall[]>('CallList')
  const [sipConnected] = useSharedVariable<boolean>('SipConnected')

  // Use callList for all call state (single source of truth)
  // Convert to CallQueueItem format for compatibility
  const allCalls = useMemo(() => {
    return (sharedCallList || []).map(
      (call) =>
        ({
          ...call,
          _id: call.callUuid,
          type: PopupQueueItemTypes.CALL,
        }) as CallQueueItem,
    )
  }, [sharedCallList])

  const otherCalls = allCalls.filter((call) => call._id !== callItem._id)
  const hasOtherCalls = otherCalls.length > 0
  const hasConnectedCalls = otherCalls.some(
    (call) =>
      'status' in call &&
      call.status === PhoneCallStatus.CONNECTED &&
      !call.isOnHold,
  )
  const hasCallsOnHold = otherCalls.some(
    (call) => 'isOnHold' in call && call.isOnHold,
  )

  // Check if other calls are only ringing (not connected)
  const hasOnlyRingingCalls = otherCalls.every(
    (call) =>
      'status' in call &&
      call.status != null &&
      isPreConnectedStatus(call.status),
  )

  const hasMaxCalls = otherCalls.length >= 2

  const isSoftphoneCall = callItem?.callSource === 'softphone'

  // Determine if call controls should be shown
  // Call controls only apply to softphone calls when SIP is connected
  const shouldShowCallControls = isSoftphoneCall && sipConnected

  // Show basic Accept/Reject when:
  // - Call controls are enabled (softphone + SIP connected), AND
  // - No other calls exist, OR
  // - Only on-hold calls exist (no active connected calls), OR
  // - Only ringing calls exist (no connected calls, no held calls)
  const shouldShowBasicButtons =
    shouldShowCallControls &&
    (!hasOtherCalls ||
      (!hasConnectedCalls && (hasCallsOnHold || hasOnlyRingingCalls)))
  const shouldShowHoldAndAccept =
    shouldShowCallControls &&
    hasOtherCalls &&
    (hasConnectedCalls || hasCallsOnHold) &&
    !hasMaxCalls
  const shouldShowEndAndAccept =
    shouldShowCallControls && hasOtherCalls && hasConnectedCalls

  // Use CallQueue item directly (now contains real-time SIP state)
  const isConnectedCall = callItem?.status === PhoneCallStatus.CONNECTED
  const isOnHold = callItem?.isOnHold ?? false

  // Track if accept or end was clicked to prevent flickering
  const [acceptClicked, setAcceptClicked] = useState(false)

  const handleAnswer = (
    queueItem: CallQueueItem,
    answerType: CallAnswerType = 'answer',
  ) => {
    if (queueItem) {
      setAcceptClicked(true)
      const callUuid = queueItem.callUuid

      if (effectiveCallPopEnabled) {
        const answerEventMap: Record<CallAnswerType, WebPhoneAnalyticsEvents> =
          {
            answer: WebPhoneAnalyticsEvents.PopupAnswerClicked,
            answerWithHold: WebPhoneAnalyticsEvents.PopupHoldAndAcceptClicked,
            answerWithEnd: WebPhoneAnalyticsEvents.PopupEndAndAcceptClicked,
            answerWithPark: WebPhoneAnalyticsEvents.PopupParkAndAcceptClicked,
          }
        track(answerEventMap[answerType])

        switch (answerType) {
          case 'answerWithHold':
            answerCallWithHold(callUuid)
            break
          case 'answerWithEnd':
            answerCallWithEnd(callUuid)
            break
          case 'answerWithPark':
            break
          default:
            answerPhoneCall(callUuid)
            break
        }
      }
    }
  }

  const handleReject = (queueItem: CallQueueItem) => {
    if (queueItem) {
      const callUuid = queueItem.callUuid
      if (effectiveCallPopEnabled) {
        // Use abstracted method from useMainWindow
        rejectPhoneCall(callUuid)
      }
    }
  }

  const handleHoldToggle = (queueItem: CallQueueItem) => {
    const callUuid = queueItem.callUuid || ''
    if (effectiveCallPopEnabled) {
      // Use abstracted methods from useMainWindow
      if (isOnHold) {
        resumeCall(callUuid)
      } else {
        holdCall(callUuid)
      }
    }
  }

  const handleEnd = (queueItem: CallQueueItem) => {
    const callUuid = queueItem.callUuid || ''

    if (effectiveCallPopEnabled) {
      // Use abstracted method from useMainWindow
      endCall(callUuid)
    }
  }

  const handleSwitchCall = (queueItem: CallQueueItem) => {
    const callUuid = queueItem.callUuid || ''

    if (!callUuid || !isOnHold || !hasConnectedCalls) {
      return
    }

    if (effectiveCallPopEnabled) {
      // Use abstracted method from useMainWindow
      switchCall(callUuid)
    }
  }

  const handleDismiss = (queueItem: CallQueueItem) => {
    const callUuid = queueItem.callUuid || ''
    if (callUuid) {
      dismissCall(callUuid)
    }
  }

  return (
    <Flex
      direction="column"
      flex="1"
      rowGap={2}
      flexGrow={1}
      paddingTop={1}
      data-testid="popup-call-buttons"
      {...rest}
    >
      {/* Main call function buttons row */}
      <Flex
        direction="row"
        wrap="wrap"
        columnGap={2}
        rowGap={2}
        justifyContent="space-evenly"
        alignItems="center"
      >
        {/* Show connected call buttons for CONNECTED softphone calls */}
        {isConnectedCall && shouldShowCallControls && (
          <>
            {/* Show Switch button if this call is on hold and there are other connected calls */}
            {isOnHold && hasConnectedCalls && (
              <MangoButtonPopup
                leftIcon={ArrowUpDown}
                leftIconProps={{
                  boxSize: 6,
                }}
                onClick={() => handleSwitchCall(callItem)}
                title={t('popupCallButtons.switch')}
                ariaLabel={t('popupCallButtons.tooltip.switch')}
                variant={MangoButtonVariant.Default_300}
              />
            )}
            {/* Show Resume button for held calls when no other connected calls */}
            {isOnHold && !hasConnectedCalls && (
              <MangoButtonPopup
                leftIcon={Play}
                onClick={() => handleHoldToggle(callItem)}
                title={t('popupCallButtons.resume')}
                ariaLabel={t('popupCallButtons.tooltip.resume')}
                variant={MangoButtonVariant.Neutral_300}
              />
            )}
            {/* Show Hold button for active calls */}
            {!isOnHold && (
              <MangoButtonPopup
                leftIcon={Pause}
                onClick={() => handleHoldToggle(callItem)}
                title={t('callControlButton.hold')}
                variant={MangoButtonVariant.Neutral_300}
              />
            )}
            <MangoButtonPopup
              leftIcon={PhoneOff}
              onClick={() => handleEnd(callItem)}
              title={t('popupCallButtons.end')}
              ariaLabel={t('popupCallButtons.tooltip.end')}
              variant={MangoButtonVariant.Destructive_300}
            />
          </>
        )}

        {/* Show desk phone indicator + dismiss for connected deskphone calls */}
        {isConnectedCall && callItem?.callSource === 'deskphone' && (
          <Flex
            width={'100%'}
            gap={2}
            alignItems={'center'}
            justifyContent={'space-between'}
          >
            <Text
              css={{
                fontSize: 'x-small',
                color: 'fg.subtle',
              }}
              flexGrow={2}
            >
              {t('popup.callButtons.deskPhoneActive')}
            </Text>
            <MangoButtonPopup
              leftIcon={X}
              onClick={() => handleDismiss(callItem)}
              title={t('popup.callButtons.dismiss')}
              variant={MangoButtonVariant.Neutral_300}
            />
          </Flex>
        )}

        {/* Show basic Accept/Reject buttons for ringing softphone calls */}
        {!isConnectedCall &&
          !acceptClicked &&
          shouldShowBasicButtons &&
          callItem?.status !== PhoneCallStatus.CONNECTED && (
            <>
              <MangoButtonPopup
                onClick={() => handleAnswer(callItem, 'answer')}
                title={t('popupCallButtons.accept')}
                ariaLabel={t('popupCallButtons.tooltip.accept')}
                variant={MangoButtonVariant.Success_300}
              />
              <MangoButtonPopup
                leftIcon={X}
                onClick={() => handleReject(callItem)}
                title={t('popupCallButtons.reject')}
                ariaLabel={t('popupCallButtons.tooltip.reject')}
                variant={MangoButtonVariant.Destructive_300}
              />
            </>
          )}

        {/* Show End & Accept for ringing softphone calls when there are connected calls */}
        {!isConnectedCall &&
          !acceptClicked &&
          shouldShowEndAndAccept &&
          callItem?.status !== PhoneCallStatus.CONNECTED && (
            <>
              <MangoButtonPopup
                leftIcon={PhoneOff}
                onClick={() => handleAnswer(callItem, 'answerWithEnd')}
                title={t('popupCallButtons.endAndAccept')}
                ariaLabel={t('popupCallButtons.tooltip.endAndAccept')}
                variant={MangoButtonVariant.Success_300}
              />
              <MangoButtonPopup
                leftIcon={X}
                onClick={() => handleReject(callItem)}
                title={t('popupCallButtons.reject')}
                ariaLabel={t('popupCallButtons.tooltip.reject')}
                variant={MangoButtonVariant.Destructive_300}
              />
            </>
          )}

        {/* Show Hold & Accept and Park & Accept for ringing softphone calls when there are other calls */}
        {!isConnectedCall && !acceptClicked && shouldShowHoldAndAccept && (
          <>
            <MangoButtonPopup
              leftIcon={Pause}
              onClick={() => handleAnswer(callItem, 'answerWithHold')}
              title={t('popupCallButtons.holdAndAccept')}
              ariaLabel={t('popupCallButtons.tooltip.holdAndAccept')}
              variant={MangoButtonVariant.Success_300}
            />
            <ParkCallButton
              isInPopup
              variant={IncomingCallButtonVariants.ParkAndAccept}
              onClick={noop}
              onClickParkingLot={(parkingLot: TrackedParkingLot) => {
                if (effectiveCallPopEnabled) {
                  // Use answerCallWithParkCurrent from useMainWindow - this handles the Park & Accept functionality
                  answerCallWithParkCurrent(
                    parkingLot.targetNumber,
                    callItem.callUuid,
                  )

                  // Note: callList will be updated automatically by main window via IPC
                  // No optimistic update needed - popup is now read-only view
                  setAcceptClicked(true)
                }
              }}
              parkingLots={parkingLots || []}
              mangoButtonProps={{
                ...buttonPropsWithoutIcons,
                size: Size.sm,
                variant: MangoButtonVariant.Success_300,
              }}
            >
              {t('popupCallButtons.parkAndAccept')}
            </ParkCallButton>
          </>
        )}

        {/* Dismiss button for ringing deskphone calls */}
        {!isConnectedCall && callItem?.callSource === 'deskphone' && (
          <Flex width={'100%'} justifyContent={'flex-end'}>
            <MangoButtonPopup
              leftIcon={X}
              onClick={() => handleDismiss(callItem)}
              title={t('popup.callButtons.dismiss')}
              variant={MangoButtonVariant.Neutral_300}
              customMangoButtonProps={{
                flexGrow: 0,
                flexShrink: 1,
                maxWidth: '40px',
              }}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
